/**
 * Search and filtering for the fleet screens — the single source of truth for
 * "which vehicles / soldiers does this query show?".
 *
 * The logic lives here rather than inside the components so the roster screens
 * stay presentational and the rules can be tested directly by the healthcheck.
 *
 * The search box takes what people actually type: a three-digit internal
 * number (`220`), part of a full military plate (`74-118`), a name, a personal
 * number or a phone. A query that resolves to a three-digit id is treated as
 * an id hit and reported separately, so the card can highlight the badge the
 * operator was looking for.
 *
 * The internal number is a vehicle's alone — a soldier is found by name,
 * personal number, phone, role or platoon.
 */
import { extractMilitaryId, plateTail } from './military-id';
import { normalizeSoldierRole, soldierRoleLabel, soldierRoleRank } from './soldier-roles';
import {
  EMPTY_ASSIGNMENT_INDEX,
  effectiveSoldierAvailability,
  effectiveVehicleStatus,
} from './effective-status';
import type { AssignmentIndex } from './effective-status';
import { describeVehicleLocation } from './vehicle-location';
import type { VehicleLocationKind } from './vehicle-location';
import {
  VEHICLE_CATEGORY_LABELS,
  VEHICLE_STATUS_LABELS,
  VEHICLE_TYPE_LABELS,
  SOLDIER_AVAILABILITY_LABELS,
} from '../types/fleet';
import type {
  Soldier,
  SoldierAvailability,
  SoldierRole,
  Vehicle,
  VehicleCategory,
  VehicleStatus,
  VehicleType,
} from '../types/fleet';

/** The "no filter" option every dropdown and tab strip carries. */
export const ANY = 'all';

export type Any = typeof ANY;

// ── Query parsing ────────────────────────────────────────────────────

export interface FleetSearchQuery {
  /** Exactly what was typed, kept for rendering the empty state. */
  raw: string;
  /** Lower-cased and trimmed — the substring the fields are matched against. */
  text: string;
  /** Digits only, for plate and phone lookups. */
  digits: string;
  /** The three-digit internal number the query resolves to, if any. */
  militaryId: string | null;
}

/** Turns raw box input into the shape every matcher below reads. */
export function parseFleetSearch(raw: string | null | undefined): FleetSearchQuery {
  const source = String(raw ?? '');
  const text = source.trim().toLowerCase();
  return {
    raw: source,
    text,
    digits: text.replace(/\D/g, ''),
    militaryId: extractMilitaryId(source),
  };
}

const norm = (value: string | null | undefined): string => String(value ?? '').toLowerCase();

const digitsOf = (value: string | null | undefined): string =>
  String(value ?? '').replace(/\D/g, '');

/** True when any of the fields contains the typed text. */
function hitsAnyField(fields: readonly (string | null | undefined)[], text: string): boolean {
  return fields.some((field) => norm(field).includes(text));
}

// ── Vehicles ─────────────────────────────────────────────────────────

/**
 * The tab strip above the grid — the five ways the dispatcher reads the pool.
 *
 * It is one control, not two: three of the tabs ask where a vehicle *is* and
 * two ask which pool it belongs to, and the operator only ever wants one of
 * those questions answered at a time. Keeping it a single `scope` is what lets
 * the strip stay a strip.
 */
export const VEHICLE_SCOPES = ['all', 'home', 'loaned', 'yaram', 'brown'] as const;

export type VehicleScope = (typeof VEHICLE_SCOPES)[number];

export const VEHICLE_SCOPE_LABELS: Record<VehicleScope, string> = {
  all: 'הכל',
  home: 'זמינים בשינוע',
  loaned: 'מוקצים לפלוגות',
  yaram: 'רכבי יר"ם',
  brown: 'רכבים חומים',
};

export interface VehicleFilters {
  text: string;
  /** Which tab of the strip is open. */
  scope: VehicleScope;
  category: VehicleCategory | Any;
  vehicleType: VehicleType | Any;
  status: VehicleStatus | Any;
}

export const EMPTY_VEHICLE_FILTERS: VehicleFilters = {
  text: '',
  scope: 'all',
  category: ANY,
  vehicleType: ANY,
  status: ANY,
};

/** True when the vehicle belongs under that tab. */
export function matchesVehicleScope(vehicle: Vehicle, scope: VehicleScope): boolean {
  switch (scope) {
    case 'home':
      return describeVehicleLocation(vehicle).kind === 'home';
    case 'loaned':
      return describeVehicleLocation(vehicle).kind === 'loaned';
    case 'yaram':
      return vehicle.category === 'yaram';
    case 'brown':
      return vehicle.category === 'brown';
    default:
      return true;
  }
}

/**
 * True when the query is the number the vehicle is called by — its internal
 * number, or the tail of its מספר צ when no internal number has been recorded.
 * That is exactly what the card puts in its badge, so the highlight always
 * lands on the digits the operator typed.
 */
export function isVehicleIdHit(vehicle: Vehicle, query: FleetSearchQuery): boolean {
  if (query.militaryId === null) return false;
  if (vehicle.militaryId) return vehicle.militaryId === query.militaryId;
  return plateTail(vehicle.plateNumber) === query.militaryId;
}

export function matchesVehicleSearch(
  vehicle: Vehicle,
  query: FleetSearchQuery,
  index: AssignmentIndex = EMPTY_ASSIGNMENT_INDEX,
): boolean {
  if (!query.text) return true;
  if (isVehicleIdHit(vehicle, query)) return true;
  if (query.digits.length >= 2 && digitsOf(vehicle.plateNumber).includes(query.digits)) return true;
  return hitsAnyField(
    [
      vehicle.name,
      vehicle.model,
      vehicle.plateNumber,
      vehicle.militaryId,
      vehicle.company,
      vehicle.assignedCompany,
      vehicle.notes,
      VEHICLE_TYPE_LABELS[vehicle.vehicleType],
      VEHICLE_CATEGORY_LABELS[vehicle.category],
      // The status the operator can see on the card — "במשימה" finds a vehicle
      // that is out even though its own record still says זמין.
      VEHICLE_STATUS_LABELS[effectiveVehicleStatus(vehicle, index)],
      // "מוקצה לפלוגת פלק״ד" is what the operator sees, so it is searchable.
      describeVehicleLocation(vehicle).label,
    ],
    query.text,
  );
}

/**
 * Where a vehicle sits in the roster: the ones standing with us first, then the
 * ones out on loan, then the ones in the workshop.
 *
 * That is the order a dispatcher asks the question in — "what can go out
 * now?" — so it is the order the screen answers in, whichever tab is open.
 */
const VEHICLE_LOCATION_ORDER: Record<VehicleLocationKind, number> = {
  home: 0,
  loaned: 1,
  maintenance: 2,
};

/** The rank {@link sortVehiclesForRoster} groups by. */
export const vehicleRosterRank = (vehicle: Vehicle): number =>
  VEHICLE_LOCATION_ORDER[describeVehicleLocation(vehicle).kind];

/**
 * The pool in the order the screen shows it — available first.
 *
 * A display decision, not a data one: `listVehicles()` still reads the pool by
 * the number each vehicle is called by, and the grouping is applied on the way
 * to the screen. The sort is stable, so inside a group the vehicles keep that
 * numeric order rather than being shuffled into a second one.
 */
export function sortVehiclesForRoster(vehicles: readonly Vehicle[]): Vehicle[] {
  return [...vehicles].sort((a, b) => vehicleRosterRank(a) - vehicleRosterRank(b));
}

/**
 * The vehicles a query shows, already in roster order — see
 * {@link sortVehiclesForRoster}, which is why the screen never re-sorts them.
 *
 * The status filter reads the *effective* status, so "במשימה" lists the
 * vehicles that are actually out on an active task and "זמין" stops offering
 * them. Passing no index reads the stored statuses, which is what a screen that
 * has not loaded the board does.
 */
export function filterVehicles(
  vehicles: readonly Vehicle[],
  filters: Partial<VehicleFilters> = {},
  index: AssignmentIndex = EMPTY_ASSIGNMENT_INDEX,
): Vehicle[] {
  const merged = { ...EMPTY_VEHICLE_FILTERS, ...filters };
  const query = parseFleetSearch(merged.text);
  const matched = vehicles.filter((vehicle) => {
    if (!matchesVehicleScope(vehicle, merged.scope)) return false;
    if (merged.category !== ANY && vehicle.category !== merged.category) return false;
    if (merged.vehicleType !== ANY && vehicle.vehicleType !== merged.vehicleType) return false;
    if (merged.status !== ANY && effectiveVehicleStatus(vehicle, index) !== merged.status) {
      return false;
    }
    return matchesVehicleSearch(vehicle, query, index);
  });
  return sortVehiclesForRoster(matched);
}

/** Summary counters for the tab strip above the grid, effective status first. */
export function vehicleStatusCounts(
  vehicles: readonly Vehicle[],
  index: AssignmentIndex = EMPTY_ASSIGNMENT_INDEX,
): Record<VehicleStatus | Any, number> {
  const counts: Record<string, number> = {
    [ANY]: vehicles.length,
    available: 0,
    in_use: 0,
    maintenance: 0,
    grounded: 0,
  };
  for (const vehicle of vehicles) {
    const status = effectiveVehicleStatus(vehicle, index);
    counts[status] = (counts[status] ?? 0) + 1;
  }
  return counts as Record<VehicleStatus | Any, number>;
}

export function vehicleCategoryCounts(
  vehicles: readonly Vehicle[],
): Record<VehicleCategory | Any, number> {
  const counts: Record<string, number> = { [ANY]: vehicles.length, yaram: 0, brown: 0 };
  for (const vehicle of vehicles) counts[vehicle.category] = (counts[vehicle.category] ?? 0) + 1;
  return counts as Record<VehicleCategory | Any, number>;
}

/**
 * The number beside every tab. A vehicle is counted under each scope it
 * answers to, so `home` + `loaned` need not add up to `all` — a vehicle in the
 * workshop is neither, and every vehicle is also either יר"ם or חום.
 */
export function vehicleScopeCounts(vehicles: readonly Vehicle[]): Record<VehicleScope, number> {
  const counts = { all: vehicles.length, home: 0, loaned: 0, yaram: 0, brown: 0 };
  for (const vehicle of vehicles) {
    for (const scope of VEHICLE_SCOPES) {
      if (scope !== 'all' && matchesVehicleScope(vehicle, scope)) counts[scope] += 1;
    }
  }
  return counts;
}

/** How many vehicles are standing in the workshop, wherever they stand. */
export function vehicleMaintenanceCount(vehicles: readonly Vehicle[]): number {
  return vehicles.filter((vehicle) => describeVehicleLocation(vehicle).kind === 'maintenance')
    .length;
}

// ── Soldiers ─────────────────────────────────────────────────────────

export interface SoldierFilters {
  text: string;
  availability: SoldierAvailability | Any;
  role: SoldierRole | Any;
}

export const EMPTY_SOLDIER_FILTERS: SoldierFilters = {
  text: '',
  availability: ANY,
  role: ANY,
};

export function matchesSoldierSearch(soldier: Soldier, query: FleetSearchQuery): boolean {
  if (!query.text) return true;
  if (query.digits.length >= 3) {
    if (digitsOf(soldier.phone).includes(query.digits)) return true;
    if (digitsOf(soldier.personalNumber).includes(query.digits)) return true;
  }
  return hitsAnyField(
    [
      soldier.fullName,
      soldier.personalNumber,
      soldier.phone,
      soldier.company,
      soldier.platoon,
      soldier.notes,
      soldier.role,
      soldierRoleLabel(soldier.role),
      SOLDIER_AVAILABILITY_LABELS[soldier.availability],
    ],
    query.text,
  );
}

/**
 * The soldiers the company reads first *within their own role rank*, in the
 * order it reads them. All three are נהגים, so the pin leads the נהג block —
 * it never lifts anyone over the chain of command.
 *
 * This is a display decision, not a data one: the roster is still stored and
 * listed alphabetically by `listSoldiers()`, and the pin is applied on the way
 * to the screen. Matched on the full name, which is what the unit named.
 */
export const PINNED_SOLDIER_NAMES: readonly string[] = ['נתי כהן', 'אשר אמר', 'שרה חיון'];

const PINNED_RANK: ReadonlyMap<string, number> = new Map(
  PINNED_SOLDIER_NAMES.map((name, index) => [name, index]),
);

/** Where a soldier sits in the pinned block — everyone else sorts after it. */
const pinnedRank = (soldier: Soldier): number =>
  PINNED_RANK.get(String(soldier.fullName ?? '').trim()) ?? PINNED_RANK.size;

/**
 * Available first. Read from the *effective* availability, so a soldier who is
 * "בבית עד יום שלישי" drops out of the top block without their stored record
 * being touched, and rises back into it by themselves when the status lapses.
 */
export const soldierRosterRank = (
  soldier: Soldier,
  now: number = Date.now(),
  index: AssignmentIndex = EMPTY_ASSIGNMENT_INDEX,
): number => (effectiveSoldierAvailability(soldier, index, now) === 'available' ? 0 : 1);

/**
 * Where the soldier's job puts them in the reading order — `1` for the מ"פ down
 * to `6` for the נהג. Read through `normalizeSoldierRole()`, so a record an
 * older build filed under a job the roster no longer keeps still ranks (as a
 * נהג) instead of falling off the bottom of the list.
 */
export const soldierHierarchyRank = (soldier: Soldier): number => soldierRoleRank(soldier.role);

/**
 * The roster in the order the company reads it — by the chain of command first,
 * and only then by who is standing:
 *
 * 1. **Role.** מ"פ, סמ"פ, פקיד/ה, קצין רכב, רס"פ, נהג — the order
 *    `SOLDIER_ROLES` is written in. Nothing crosses a rank boundary.
 * 2. **Status.** Inside a rank, the available soldiers lead. Read from the
 *    *effective* availability, so a soldier who is "בבית עד יום שלישי" drops to
 *    the foot of their own rank and rises back by themselves.
 * 3. **The pin.** The three names {@link PINNED_SOLDIER_NAMES} holds, in that
 *    order — all נהגים, so the pin only ever orders the נהג block.
 * 4. **Alphabetically** by full name, in Hebrew.
 *
 * A display decision, not a data one: the roster is still stored and listed
 * alphabetically by `listSoldiers()`, and the order is applied on the way to
 * the screen.
 */
export function sortSoldiersForRoster(
  soldiers: readonly Soldier[],
  now: number = Date.now(),
  index: AssignmentIndex = EMPTY_ASSIGNMENT_INDEX,
): Soldier[] {
  return [...soldiers].sort((a, b) => {
    const byRole = soldierHierarchyRank(a) - soldierHierarchyRank(b);
    if (byRole !== 0) return byRole;
    const byStatus = soldierRosterRank(a, now, index) - soldierRosterRank(b, now, index);
    if (byStatus !== 0) return byStatus;
    const byPin = pinnedRank(a) - pinnedRank(b);
    if (byPin !== 0) return byPin;
    return String(a.fullName ?? '').localeCompare(String(b.fullName ?? ''), 'he');
  });
}

/**
 * The status filter reads the *effective* availability, so a soldier who is
 * "בבית עד יום שלישי" is filed as unavailable and one riding an active task is
 * filed as במשימה — in both cases without their stored record being rewritten.
 *
 * What comes back is already in roster order — see
 * {@link sortSoldiersForRoster} — so the screen never re-sorts it.
 */
export function filterSoldiers(
  soldiers: readonly Soldier[],
  filters: Partial<SoldierFilters> = {},
  now: number = Date.now(),
  index: AssignmentIndex = EMPTY_ASSIGNMENT_INDEX,
): Soldier[] {
  const merged = { ...EMPTY_SOLDIER_FILTERS, ...filters };
  const query = parseFleetSearch(merged.text);
  const matched = soldiers.filter((soldier) => {
    // Read through the normaliser, so a record still filed under a retired job
    // is found under נהג — the same job the roster shows for it.
    if (merged.role !== ANY && normalizeSoldierRole(soldier.role) !== merged.role) return false;
    if (
      merged.availability !== ANY &&
      effectiveSoldierAvailability(soldier, index, now) !== merged.availability
    ) {
      return false;
    }
    return matchesSoldierSearch(soldier, query);
  });
  return sortSoldiersForRoster(matched, now, index);
}

export function soldierAvailabilityCounts(
  soldiers: readonly Soldier[],
  now: number = Date.now(),
  index: AssignmentIndex = EMPTY_ASSIGNMENT_INDEX,
): Record<SoldierAvailability | Any, number> {
  const counts: Record<string, number> = {
    [ANY]: soldiers.length,
    available: 0,
    on_mission: 0,
    leave: 0,
    unavailable: 0,
  };
  for (const soldier of soldiers) {
    const state = effectiveSoldierAvailability(soldier, index, now);
    counts[state] = (counts[state] ?? 0) + 1;
  }
  return counts as Record<SoldierAvailability | Any, number>;
}
